// ** React Imports
import { useState, useEffect } from "react"
import { Link, useSearchParams } from "react-router-dom"
// ** Third Party Components
import ReactPaginate from "react-paginate"
import { ChevronDown, Edit2, ExternalLink, MoreVertical, Search, Trash2 } from "react-feather"
import DataTable from "react-data-table-component"
// ** Custom Components
import Avatar from "@components/avatar"
// ** Reactstrap Imports
import {
  Input,
  Row,
  Col,
  Card,
  Badge,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap"
// ** Store & Actions
import { useDispatch, useSelector } from "react-redux"
import { getData } from "@store/stores/visit"
// ** Styles
import "@styles/react/apps/app-invoice.scss"
import "@styles/react/libs/tables/react-dataTable-component.scss"
// ** Utils
import { getAllFromController } from "@lib/api"
import { convertNameToColor, dateISO, eventColorSelection } from "@lib/util"
import { ANONYMOUS_CLIENT } from "@lib/constants"
import ExpandableRow from "./ExpandableRow"
import DrawerPerson from "../../components/DrawerPerson"
import DeleteConfirmation from "../../components/DeleteConfirmation"

const CustomHeader = ({
  searchHandler,
  changeEventReasonValueHandler,
  changeShowroomIdValueHandler,
  searchValue,
  showroomIdValue,
  handlePerPage,
  rowsPerPage,
  showrooms,
  eventReasons
}) => {
  return (
    <div className="invoice-list-table-header w-100 py-2">
      <Row>
        <Col lg="4" className="d-flex align-items-center px-0 px-lg-1">
          <div className="d-flex align-items-center me-2">
            <label htmlFor="rows-per-page">Show</label>
            <Input
              type="select"
              id="rows-per-page"
              value={rowsPerPage}
              onChange={handlePerPage}
              className="form-control ms-50 pe-3"
            >
              <option value="20">20</option>
              <option value="35">35</option>
              <option value="50">50</option>
            </Input>
          </div>
        </Col>
        <Col
          lg="8"
          className="actions-right d-flex align-items-center justify-content-lg-end flex-lg-nowrap flex-wrap mt-lg-0 pe-lg-1 p-0 gap-2"
        >
          <div className="d-flex align-items-center gap-1">
            <label htmlFor="search-client">Search</label>
            <Input
              id="search-client"
              className="w-auto"
              type="text"
              defaultValue={searchValue}
              onChange={(e) => searchHandler(e.target.value)}
              placeholder="Name, CFG_ or description"
            />
          </div>
          <div>
            {showrooms ? (
              <Input
                className="w-auto"
                type="select"
                onChange={changeShowroomIdValueHandler}
                defaultValue={showroomIdValue}
              >
                <option value={0}>All Showrooms</option>
                {showrooms.map((showroom) => (
                  <option key={showroom.id} value={showroom.id}>
                    {showroom.name}
                  </option>
                ))}
              </Input>
            ) : (
              <span className="text-muted">Loading showrooms...</span>
            )}
          </div>
          <div>
            <Input className="w-auto" type="select" onChange={changeEventReasonValueHandler}>
              <option value={eventReasons.map((reason) => reason.id).join(",")}>All types</option>
              <option value="51,52">Visits</option>
              {eventReasons.map((eventReason) => {
                return (
                  <option key={eventReason.id} value={eventReason.id}>
                    {eventReason.name}
                  </option>
                )
              })}
            </Input>
          </div>
        </Col>
      </Row>
    </div>
  )
}

const VisitsList = () => {
  // ** vars
  const dispatch = useDispatch()
  const userStore = useSelector((state) => state.users)
  const visistStore = useSelector((state) => state.visits)
  const [eventReasons, setEventReasons] = useState([])
  const [showrooms, setShowrooms] = useState()
  const [drawer, setDrawer] = useState({
    isOpen: false,
    personId: null
  })

  // ** Hooks for query params (URL)
  const [searchParams, setSearchParams] = useSearchParams()
  const showroomId = parseInt(searchParams.get("showroomId")) || 0
  const page = parseInt(searchParams.get("page")) || 1
  // ** Params for DataTable component and subsequent API calls
  const [dataTableParams, setDataTableParams] = useState({
    q: null,
    sort: "desc",
    sortColumn: "on",
    page,
    perPage: 20,
    eventReasonId: null,
    showroomId
  })

  useEffect(() => {
    getAllFromController("Events/Reasons", "visits").then((res) => {
      // remove the first 10 reasons (system reserved)
      const publicEventReasons = res.data.filter((e) => e.id >= 10)
      setEventReasons(publicEventReasons)
      // update default table filter with those events
      const newdataTableParams = {
        ...dataTableParams,
        eventReasonId: publicEventReasons.map((reason) => reason.id).join(",")
      }
      setDataTableParams(newdataTableParams)
    })
    getAllFromController("Showrooms").then((res) => setShowrooms(res.data))
  }, [])

  // ** init and update data each time params change
  useEffect(() => {
    dispatch(getData(dataTableParams))
  }, [dispatch, visistStore.data.length, dataTableParams])

  // ** Drawer specific functions
  const toggleDrawer = () => {
    setDrawer((prevState) => ({ ...prevState, isOpen: !prevState.isOpen }))
  }
  const openDrawer = (personId) => {
    setDrawer((prevState) => ({ ...prevState, isOpen: true, personId }))
  }

  // ** Functions to handle filter changes
  const searchHandler = (val) => {
    const newdataTableParams = { ...dataTableParams, q: val, page: 1 }
    setDataTableParams(newdataTableParams)
  }
  const handlePerPage = (e) => {
    const newdataTableParams = { ...dataTableParams, perPage: parseInt(e.target.value), page: 1 }
    setDataTableParams(newdataTableParams)
  }
  const changeShowroomIdValueHandler = (e) => {
    const showroomId = parseInt(e.target.value)
    const newdataTableParams = { ...dataTableParams, showroomId, page: 1 }
    setSearchParams((params) => {
      params.set("showroomId", showroomId)
      return params
    })
    setDataTableParams(newdataTableParams)
  }
  const changeEventReasonValueHandler = (e) => {
    const newdataTableParams = { ...dataTableParams, eventReasonId: e.target.value, page: 1 }
    setDataTableParams(newdataTableParams)
  }
  const handlePagination = (page) => {
    const newdataTableParams = { ...dataTableParams, page: page.selected + 1 }
    setDataTableParams(newdataTableParams)
    setSearchParams((params) => {
      params.set("page", page.selected + 1)
      return params
    })
  }
  const handleSort = (column, sortDirection) => {
    const newdataTableParams = {
      ...dataTableParams,
      sort: sortDirection,
      sortColumn: column.sortField
    }
    setDataTableParams(newdataTableParams)
  }

  // ** Table columns
  const columns = [
    {
      name: "Date",
      sortable: true,
      sortField: "on",
      minWidth: "120px",
      maxWidth: "125px",
      cell: (row) => dateISO(row.on, true, true)
    },
    {
      name: "Client",
      minWidth: "180px",
      cell: (row) => {
        const name = row.interaction.person.name ? row.interaction.person.name : "",
          firstName = row.interaction.person.firstName ? row.interaction.person.firstName : ""
        if (row.interaction.personId === ANONYMOUS_CLIENT.id) {
          return (
            <div
              style={{ pointerEvents: "none" }}
              className="d-flex justify-content-left align-items-center"
            >
              <div className="d-flex flex-column">
                <h6 className="user-name mb-0 fst-italic fw-normal">
                  {name} {firstName}
                </h6>
              </div>
            </div>
          )
        } else {
          return (
            <div
              style={{ pointerEvents: "none" }}
              className="d-flex justify-content-left align-items-center"
            >
              <Avatar
                color={`light-${convertNameToColor(name)}`}
                className="me-1"
                content={`${name} ${firstName}`}
                initials
              />
              <div className="d-flex flex-column">
                <h6 className="user-name mb-0">
                  {name.toUpperCase()} {firstName}
                </h6>
              </div>
            </div>
          )
        }
      }
    },
    {
      sortable: true,
      name: "Amount",
      maxWidth: "130px",
      minWidth: "130px",
      sortField: "amount",
      cell: (row) => {
        if (row.amount > 0) {
          return (
            <span>
              {row.currency?.iso3} {row.amount}
            </span>
          )
        } else {
          return "-"
        }
      }
    },
    {
      sortable: true,
      name: "Event",
      minWidth: "360px",
      sortField: "eventReasonId",
      cell: (row) => {
        const TRUNCATE_LENGTH = 100
        const truncatedDesc = `${
          row.description ? row.description.substring(0, TRUNCATE_LENGTH) : ""
        }${row.description?.length >= TRUNCATE_LENGTH ? "..." : ""}`
        return (
          <div style={{ pointerEvents: "none" }}>
            <Badge color={`light-${eventColorSelection(row.eventReason.id)}`} pill className="me-1">
              {row.eventReason.name}
              {row.configOrderId && ` CFG_${row.configOrderId}`}
            </Badge>
            {row.description && <small style={{ color: "#777" }}>{truncatedDesc}</small>}
          </div>
        )
      }
    },
    {
      sortable: true,
      name: "Collaborator",
      sortField: "collaboratorId",
      // minWidth: "140px",
      maxWidth: "200px",
      cell: (row) => (
        <div className="user-info text-truncate" style={{ pointerEvents: "none" }}>
          <span className="d-block fw-bold text-truncate">
            {row.collaborator.firstName[0]} {row.collaborator.name}
          </span>
          <small>{row.showroom?.name.replace("Elite Gallery ", "")}</small>
        </div>
      )
    },
    {
      name: "",
      minWidth: "80px",
      maxWidth: "8px",
      cell: (row) => (
        <div className="column-action">
          <UncontrolledDropdown>
            <DropdownToggle tag="div" className="btn btn-sm">
              <MoreVertical size={18} className="cursor-pointer" />
            </DropdownToggle>
            <DropdownMenu>
              {row.configOrderId && (
                <DropdownItem
                  className="w-100"
                  style={{ borderBottom: "1px solid #ddd" }}
                  tag={Link}
                  to={`/Configurator/Orders/Details/${row.configOrderId}`}
                  target="_blank"
                >
                  <ExternalLink size={17} className="me-50" />
                  {row.eventReason.id < 20 ? "Offer " : "Sale "}
                  {row.configOrderId}
                </DropdownItem>
              )}
              {row.interaction.personId !== ANONYMOUS_CLIENT.id && (
                <>
                  <DropdownItem
                    className="w-100"
                    onClick={() => openDrawer(row.interaction.personId)}
                  >
                    <Search size={14} className="me-50" />
                    <span className="align-middle">View person</span>
                  </DropdownItem>
                  <DropdownItem
                    tag={Link}
                    className="w-100"
                    to={`/CRM/Visits/${row.interaction.personId}`}
                  >
                    <Edit2 size={14} className="me-50" />
                    <span className="align-middle">Edit person</span>
                  </DropdownItem>
                </>
              )}
              {userStore.selectedUser?.roles?.CRM_Admin && (
                <DeleteConfirmation
                  id={row.configOrderId ? row.configOrderId : row.id}
                  endpoint={row.configOrderId ? "OrdersAPI" : "Events"}
                  onSuccess={() => dispatch(getData(dataTableParams))}
                >
                  <DropdownItem className="w-100" style={{ borderTop: "1px solid #ddd" }}>
                    <Trash2 size={14} className="me-50" />
                    <span className="align-middle">Delete event</span>
                  </DropdownItem>
                </DeleteConfirmation>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      )
    }
  ]
  const CustomPagination = () => {
    const count = parseInt(Math.max(1, Math.ceil(visistStore.total / dataTableParams.perPage))) || 1
    return (
      <ReactPaginate
        nextLabel=""
        breakLabel="..."
        previousLabel=""
        pageCount={count}
        activeClassName="active"
        breakClassName="page-item"
        pageClassName={"page-item"}
        breakLinkClassName="page-link"
        nextLinkClassName={"page-link"}
        pageLinkClassName={"page-link"}
        nextClassName={"page-item next"}
        previousLinkClassName={"page-link"}
        previousClassName={"page-item prev"}
        onPageChange={(page) => handlePagination(page)}
        forcePage={dataTableParams.page !== 0 ? dataTableParams.page - 1 : 0}
        containerClassName={"pagination react-paginate justify-content-center p-5"}
      />
    )
  }

  return (
    <div className="invoice-list-wrapper">
      <Card>
        <div className="invoice-list-dataTable react-dataTable">
          <DataTable
            noHeader
            pagination
            sortServer
            paginationServer
            responsive
            subHeader
            highlightOnHover
            pointerOnHover
            expandableRows
            expandOnRowClicked
            expandableRowsComponent={ExpandableRow}
            expandableRowsComponentProps={{ onSuccess: () => dispatch(getData(dataTableParams)) }}
            columns={columns}
            onSort={handleSort}
            defaultSortFieldId={1}
            defaultSortAsc={false}
            data={visistStore.data.length > 0 ? visistStore.data : []}
            sortIcon={<ChevronDown />}
            className="react-dataTable"
            paginationDefaultPage={dataTableParams.page}
            paginationComponent={CustomPagination}
            subHeaderComponent={
              <CustomHeader
                searchValue={dataTableParams.q}
                searchHandler={searchHandler}
                rowsPerPage={dataTableParams.perPage}
                showroomIdValue={showroomId}
                handlePerPage={handlePerPage}
                changeEventReasonValueHandler={changeEventReasonValueHandler}
                changeShowroomIdValueHandler={changeShowroomIdValueHandler}
                showrooms={showrooms}
                eventReasons={eventReasons}
              />
            }
          />
        </div>
      </Card>
      {drawer.personId && (
        <DrawerPerson
          personId={drawer.personId}
          isDrawerOpen={drawer.isOpen}
          toggleDrawer={toggleDrawer}
        />
      )}
    </div>
  )
}

export default VisitsList
